import {
	identity,
	includes,
	keys,
	memoize,
	noop,
	pick,
	pickBy,
	pipe,
	prop,
	union,
} from 'lodash/fp';
import React from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { getFormValues } from 'redux-form';
import storage from 'store2';

import { MARKETING, MARKETING_EXTENDED, REGISTRATION } from '@skippay/core/constants/consentSlugs';
import { COUNTRY_CODE_CZ, COUNTRY_CODE_SK } from '@skippay/core/constants/countryCodes';
import { FormattedMessage } from '@skippay/core/i18n/intl';
import { parseNumber } from '@skippay/core/utils/numberUtils';
import { isVoid } from '@skippay/core/utils/validators';

import { ValidationWarning } from 'components/form/MtvWarning';
import CheckFirstNamesCreateMutation from 'mutations/CheckFirstNamesCreateMutation.graphql';
import { validateSmartformEmail } from 'utils/smartformUtils';

export const OPTION_ALL = 'all';

export const getConsentsFromFormValues = (formValues) => {
	const consents = pick([MARKETING_EXTENDED, REGISTRATION], formValues);
	const consentsKeys = pickBy(Boolean, consents);
	const keysArray = keys(consentsKeys);

	return keysArray.includes(REGISTRATION) && !keysArray.includes(MARKETING)
		? keysArray.concat(MARKETING)
		: keysArray;
};

export const registrationErrorFormat = () => (
	<FormattedMessage
		defaultMessage="Obchodní podmínky je nutné odsouhlasit pro&nbsp;dokončení registrace."
		id="RegistrationCallbackScreen.registrationConsentMustBeApproved"
	/>
);

export const isMarketingExtendedConsentApprovedSelector = (formName) =>
	pipe(getFormValues(formName), prop(MARKETING_EXTENDED), Boolean);

export const addAllOptionToSelect = ({ options, label }) => {
	options.unshift({
		value: OPTION_ALL,
		label,
	});
	return options;
};

/**
 * A final-form mutator for triggering validation on specific field. The final-form does not provide an API for
 * triggering validation, only the `validation` function, which runs automatically on fields change or on blur when the
 * `validateOnBlur` boolean FormProp is set. The problem is, that we need validate synchronous validations on change and
 * also run the asynchronous validations on blur. This mutator is the key, use it `onBlur` and use the
 * `mutatorTriggeredValidator` utility for asynchronous validations.
 * @param {[string]} args - arguments passed to the mutator, it expects a name of the field to validate.
 * @param {import(`final-form`).MutableState} form - MutableState of the final-form
 * @param {import(`final-form`).Tools} tools modifiers of the form state
 * @see https://final-form.org/docs/final-form/types/Mutator
 */
export const triggerValidationMutator = ([fieldName], form, tools) => {
	// set flag `mutatorValidation` on the fields `meta.data` which is accessible from the validator. This allows the
	// validator determine that the validation was triggered by this mutator and for this specific field.
	// eslint-disable-next-line no-param-reassign
	form.fields[fieldName].data.mutatorValidation = true;
	// "update" the value of the specified field, to the same, unchanged value using @lodash/fp/identity -- it fakes
	// "update" to just trigger validation
	tools.changeValue(form, fieldName, identity);
	// reset the `mutatorValidation` flag in next macro-task - the validation triggered by `tools.changeValue` is not
	// synchronous
	window.setTimeout(() => {
		// the form or form-field might be destroyed when this delayed callback executes
		if (form.fields[fieldName]) {
			// eslint-disable-next-line no-param-reassign
			form.fields[fieldName].data.mutatorValidation = undefined;
		}
	});
};

/**
 * Utility complementary to the `triggerValidationMutator`, it accepts a regular final-form validator and runs it only
 * when the validation is triggered via the mutator for the specific field.
 * The validator has also this additional properties
 * - it handles both synchronous and asynchronous validations
 * - it skip validation of the initial value, as it assumes, that the initial value is correct
 * - it passes to the validator and extra argument - `clearError(): void`, which allows the validator to clear error
 *   previously created by the mutator-triggered-validator.
 * @param {function(
		 value: string | undefined,
		 allValues: object,
		 meta: import(`final-form`).FieldState<string>,
		 clearError: function(): void,
	 ): any | Promise<any>} validator
 * @return {import(`final-form`).FieldValidator<string | undefined>}
 */
export const mutatorTriggeredValidator = (validator, validateInitial) => (value, values, meta) => {
	// when there is an error and it is caused by this validator, keep it rendered until the value changes
	if (meta.error && meta.data.mtv_errorValue && value === meta.data.mtv_errorValue) {
		return meta.error;
	}
	if (
		!validateInitial &&
		(!meta.data.mutatorValidation || // skip validation when not triggered by the mutator
			meta.pristine) // skip validation of initial values
	) {
		return undefined;
	}
	const clearError = () => {
		// eslint-disable-next-line no-param-reassign
		meta.data.mtv_errorValue = undefined;
		// trigger revalidation. Need to clear the value first, as the validation is not triggered when the new value
		// equals the previous.
		meta.change(``);
		meta.change(value);
	};
	// this callback handles asynchronous validation functions via recursion
	const handleResult = (result) => {
		if (typeof result?.then === `function`) {
			return result.then(handleResult);
		}
		if (result) {
			// eslint-disable-next-line no-param-reassign
			meta.data.mtv_errorValue = value;
			if (validateInitial) {
				meta.blur();
			}
		}
		return result;
	};
	return handleResult(validator(value, values, meta, clearError));
};

/**
 * A localStorage storage for mutator triggered validators
 * @type {store.StoreAPI}
 */
export const mtvStorage = storage.namespace(`mtv`);
export const appendCustomerApprovedValue = (validatorKey, approvedValue) => {
	mtvStorage.transact(validatorKey, union([approvedValue]));
};
const FIRST_NAMES = `firstNames`;
const EMAIL = `email`;
const NATIONALITY = `nationality`;
const PHONE = `phone`;

export const nameValidation = ({ apolloClient, clearError, fullName, value }) => {
	const onConfirm = () => {
		appendCustomerApprovedValue(FIRST_NAMES, value);
		clearError();
	};
	if (!/^[\p{Letter}\s]+$/u.test(value)) {
		return (
			<ValidationWarning
				confirmMessage={
					<FormattedMessage
						defaultMessage="Je to správně."
						id="validationError.fullName.specialChars.suppress"
					/>
				}
				onConfirm={onConfirm}
				testId="confirmNameValidSpecialChars"
				warningMessage={
					<FormattedMessage
						defaultMessage="Ve vašem jméně jsme si všimli nestandardního znaku, mrkněte prosím na to."
						id="validationError.fullName.specialChars"
					/>
				}
			/>
		);
	}
	if (
		fullName &&
		!/[\p{Uppercase_Letter}][\p{Lowercase_Letter}]* [\p{Uppercase_Letter}][\p{Lowercase_Letter}]*( [\p{Uppercase_Letter}])?/u.test(
			value
		)
	) {
		return (
			<ValidationWarning
				confirmMessage={
					<FormattedMessage
						defaultMessage="Je to správně."
						id="validationError.fullName.lowerCase.suppress"
					/>
				}
				onConfirm={onConfirm}
				testId="confirmNameValidLowercase"
				warningMessage={
					<FormattedMessage
						defaultMessage="Vašemu jménu by slušela velká písmena na začátku, mrkněte prosím na to."
						id="validationError.fullName.lowerCase"
					/>
				}
			/>
		);
	}
	return apolloClient
		.mutate({
			mutation: CheckFirstNamesCreateMutation,
			variables: {
				body: { [fullName ? `fullName` : `firstName`]: value },
			},
		})
		.then(
			({
				data: {
					checkFirstNamesCreate: { isValid },
				},
			}) => {
				return isValid ? undefined : (
					<ValidationWarning
						confirmMessage={
							<FormattedMessage
								defaultMessage="Ano, mám."
								id="validationError.firstNames.suppress"
							/>
						}
						onConfirm={onConfirm}
						testId="confirmNameValid"
						warningMessage={
							<FormattedMessage
								defaultMessage="Máte toto jméno i v občanském průkazu? "
								id="validationError.firstNames"
							/>
						}
					/>
				);
			}
		);
};

/**
 * Mutator-triggered-validator for checking validity of first names. Is memoized by first arg (boolean) therefore only
 * two versions of this callback will ever exists.
 * @param {boolean} fullName - weather pass the data as `fullName`, false means pass the data as `firstName`. Check the
 * OpenAPI documentation for more detail.
 * @param {import(`@apollo/client`).Client} apolloClient - the instance of apolloClient
 * @returns {import(`final-form`).FieldValidator<string | undefined>}
 */
export const mtvFirstNameValidator = memoize((fullName, apolloClient) =>
	mutatorTriggeredValidator((value, _values, _meta, clearError) =>
		isVoid(value) || includes(value, mtvStorage.get(FIRST_NAMES))
			? undefined
			: nameValidation({ fullName, apolloClient, value, clearError })
	)
);

/**
 * Mutator-triggered-validation that checks email for common mistakes against Foxentry API.
 * @type {import(`final-form`).FieldValidator<string|undefined>}
 */
export const mtvEmailValidator = mutatorTriggeredValidator((value, _values, _meta, clearError) =>
	isVoid(value) || includes(value, mtvStorage.get(EMAIL))
		? undefined
		: validateSmartformEmail(value).then(noop, () => (
				<ValidationWarning
					confirmMessage={
						<FormattedMessage
							defaultMessage="Ano, je to v pořádku."
							id="validationError.email.suppress"
						/>
					}
					onConfirm={() => {
						appendCustomerApprovedValue(EMAIL, value);
						clearError();
					}}
					testId="confirmEmailValid"
					warningMessage={
						<FormattedMessage
							defaultMessage="Vypadá to na překlep, je e-mail opravdu správně?"
							id="validationError.email"
						/>
					}
				/>
			))
);

/**
 * Mutator-triggered-validation that checks email for common mistakes against Foxentry API.
 * @type {import(`final-form`).FieldValidator<string|undefined>}
 */
const validateNationality = (permanentAddressCountry, nationality) =>
	new Promise((resolve, reject) =>
		permanentAddressCountry === COUNTRY_CODE_SK && nationality === COUNTRY_CODE_CZ
			? reject()
			: resolve()
	);

export const permanentAddressAndNationalityValidator = mutatorTriggeredValidator(
	(value, _values, _meta, clearError) =>
		isVoid(value) || includes(value, mtvStorage.get(NATIONALITY))
			? undefined
			: validateNationality(_values.permanentAddressCountryCode, value).then(noop, () => (
					<ValidationWarning
						confirmMessage={
							<FormattedMessage
								defaultMessage="Ano, je v pořádku."
								id="validationError.nationality.suppress"
							/>
						}
						onConfirm={() => {
							appendCustomerApprovedValue(NATIONALITY, value);
							clearError();
						}}
						testId="confirmNationalityValid"
						warningMessage={
							<FormattedMessage
								defaultMessage="Vaše státní příslušnost a země trvalého pobytu se&nbsp;liší. Je to tak správně?"
								id="validationError.nationality"
							/>
						}
					/>
				)),
	true
);

const getFirstPhoneNumber = (value) => {
	switch (value.length) {
		case 9:
			return value.slice(0, 1);
		case 12:
			return value.slice(3, 4);
		case 13:
			return value.slice(4, 5);
		default:
			return false;
	}
};

const isCorrectCzechOrSlovakPhoneNumber = (value) => {
	const regexSkNumber = new RegExp(/^(421|\+421)/);
	if (value.length > 9 && regexSkNumber.test(value)) {
		return false;
	}

	const firstNumber = parseNumber(getFirstPhoneNumber(value));
	const NOT_VALIDATE_NUMBERS = [2, 3, 4, 5];
	return firstNumber && NOT_VALIDATE_NUMBERS.includes(firstNumber);
};

/**
 * Mutator-triggered-validation that checks phone number.
 * @type {import(`final-form`).FieldValidator<string|undefined>}
 */
export const phoneValidator = mutatorTriggeredValidator((value, _values, _meta, clearError) => {
	if (isVoid(value) || includes(value, mtvStorage.get(PHONE))) {
		return undefined;
	}

	if (isValidPhoneNumber(value)) {
		if (isCorrectCzechOrSlovakPhoneNumber(value)) {
			return (
				<ValidationWarning
					confirmMessage={
						<FormattedMessage defaultMessage="Ano, je." id="validationError.phone.suppress" />
					}
					onConfirm={() => {
						appendCustomerApprovedValue(PHONE, value);
						clearError();
					}}
					testId="confirmEmailValid"
					warningMessage={
						<FormattedMessage
							defaultMessage="Je toto opravdu číslo na mobilní telefon?"
							id="validationError.phone.confirmEmailValid"
						/>
					}
				/>
			);
		}

		return undefined;
	}

	if (value.length < 13) {
		return (
			<FormattedMessage
				defaultMessage="Telefonní číslo není v platném formátu."
				id="validationError.length.phone"
			/>
		);
	}

	return (
		<FormattedMessage
			defaultMessage="Zadejte prosím české nebo slovenské telefonní číslo."
			id="validationError.phone"
		/>
	);
});
